import { getLogger } from '../../../libs/logger';

const logger = getLogger('subscribeForAudioBuffer');

type Unsubscribe = () => void;

/**
 * @description To be used for getting audio level from the media stream.
 * @param audioTrack Audio track to listen to
 * @param callback Get audio buffer updates
 * @param bufferSize Customize the buffer size. By default it is sampleRate / 50
 * @param _AudioContext - Internal parameter for Dependency Injection [DO NOT USE]
 * @param _AudioWorkletNode - Internal parameter for Dependency Injection [DO NOT USE]
 * @returns {Unsubscribe} - Unsubscribe function
 */
export const subscribeForAudioBuffer = async (
  audioTrack: MediaStreamTrack,
  callback: (buffer: Float32Array) => void,
  bufferSize?: number,
  _AudioContext = AudioContext,
  _AudioWorkletNode = AudioWorkletNode,
): Promise<Unsubscribe> => {
  const audioContext = new _AudioContext();
  await audioContext.audioWorklet.addModule(
    '/v2/worklet/audioBufferProcessor.js',
  );
  const sourceNode = audioContext.createMediaStreamSource(
    new MediaStream([audioTrack]),
  );
  const audioLevelNode = new AudioWorkletNode(
    audioContext,
    'audio-buffer-worklet-processor',
    { processorOptions: { bufferSize } },
  );

  sourceNode.connect(audioLevelNode).connect(audioContext.destination);

  const onMessage = ({
    data: { buffer },
  }: {
    data: { buffer: Float32Array };
  }): void => {
    callback(buffer);
  };

  audioLevelNode.port.onmessage = onMessage;

  if (audioContext.state === 'suspended') {
    void audioContext.resume().catch((error) => {
      logger.error('[subscribeForAudioBuffer/resume] failed', error);
    });
  }

  return () => {
    audioLevelNode.port.onmessage = null;
    sourceNode.disconnect();
    audioLevelNode.disconnect();
    void audioContext.close();
  };
};
